import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { compact } from 'lodash'
import { CSSProperties, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { gqlQuery } from 'app/lib/graphqlClient'
import type { MarketingMetricsResponse, MarketingMetricsRow } from 'app/generated/graphql'
import { RevenueDetails } from '../marketing/RevenueDetails'
import { MarketingContactTable } from '../shared/MarketingContactTable'
import type { FlowAction, FlowStats } from './attributionFlowUtils'
import {
  initialFlowState,
  flowReducer,
  selectSourceRows,
  selectMediumRows,
  selectCampaignRows,
  selectStats,
  selectHasFilters,
  selectIsFiltered,
  buildFilterParam,
} from './attributionFlowUtils'
import { ScrollableList } from './ScrollableList'
import { SectionHeader } from './SectionHeader'
import { StatsCards } from './StatsCards'
import { CHANNEL_HUES, SMC_CHANNEL_GROUP_HUES, chartHueStyles } from 'lib/chartColors'
import { closeModal, showModal } from 'app/lib/modal'
import type { AttributionRowType, AttributionSummaryType } from 'app/types/attribution'
import { formatCurrency } from 'lib/formatCurrency'
import { StatsCardType } from 'types/StatsCardType'

const ATTRIBUTION_FLOW_QUERY = graphql(`
  query AttributionFlowFiltered(
    $dateRange: DateRangeInput!
    $groupBy: String!
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $showDisqualified: Boolean
  ) {
    marketingMetrics(
      dateRange: $dateRange
      groupBy: $groupBy
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      showDisqualified: $showDisqualified
    ) {
      hits {
        bucket
        leads
        mqls
        appointments
        patients
        production
        scheduledProduction
        googleSpend
        facebookSpend
        roi
        budget
      }
      summary {
        leads
        mqls
        appointments
        patients
      }
    }
  }
`)

// Re-export pure functions and types from utils file for backward compatibility
export type { FlowStatus, FlowStats, FlowState, FlowAction } from './attributionFlowUtils'
export {
  initialFlowState,
  calculateStats,
  hasAnySelection,
  flowReducer,
  selectSourceRows,
  selectMediumRows,
  selectCampaignRows,
  selectStats,
  selectHasFilters,
  selectIsFiltered,
  buildFilterParam,
} from './attributionFlowUtils'

// ============================================
// HOOK TYPES
// ============================================

export type ColumnType = `sources` | `mediums` | `campaigns`

export type AttributionFlowStats = FlowStats

export type UseAttributionFlowDataProps = {
  sourceRows: AttributionRowType[] | undefined
  mediumRows: AttributionRowType[] | undefined
  campaignRows: AttributionRowType[] | undefined
  channelRows: AttributionRowType[] | undefined
  summary: AttributionSummaryType | undefined
  fromDate: string
  toDate: string
  locationIds: string[]
  integrationStatus?: string
}

export type UseAttributionFlowDataReturn = {
  sourceRows: AttributionRowType[] | undefined
  mediumRows: AttributionRowType[] | undefined
  campaignRows: AttributionRowType[] | undefined
  selectedSource: string | null
  selectedMedium: string | null
  selectedCampaign: string | null
  isSourcesLoading: boolean
  isMediumsLoading: boolean
  isCampaignsLoading: boolean
  isSourcesFiltered: boolean
  isMediumsFiltered: boolean
  isCampaignsFiltered: boolean
  stats: AttributionFlowStats
  hasFilters: boolean
  handleSelect: (columnType: ColumnType, bucket: string | null) => void
  clearFilters: () => void
}

// ============================================
// MAIN HOOK
// ============================================

export function useAttributionFlowData({
  sourceRows: initialSourceRows,
  mediumRows: initialMediumRows,
  campaignRows: initialCampaignRows,
  channelRows: initialChannelRows,
  summary: initialSummary,
  fromDate,
  toDate,
  locationIds,
  integrationStatus,
}: UseAttributionFlowDataProps): UseAttributionFlowDataReturn {
  const [state, dispatch] = useReducer(flowReducer, initialFlowState)
  const [fetchLoading, setFetchLoading] = useState(false)
  const fetchSeqRef = useRef(0)

  useEffect(() => {
    if (state.status === `idle`) return
    if (!state.isLoading) return

    // Increment sequence to invalidate any in-flight fetches from prior selections
    const seq = ++fetchSeqRef.current

    const filters = buildFilterParam(state.selectedSource, state.selectedMedium, state.selectedCampaign)
    const dateRange = { fromDate, toDate }
    const baseVars = {
      dateRange,
      locationIds: locationIds.length ? locationIds : undefined,
      integrationStatus: integrationStatus || undefined,
      showDisqualified: false,
      filters,
    }

    type FlowGqlResponse = { marketingMetrics: MarketingMetricsResponse }

    setFetchLoading(true)
    Promise.all([
      gqlQuery<FlowGqlResponse>(ATTRIBUTION_FLOW_QUERY, { ...baseVars, groupBy: `utmSource` }),
      gqlQuery<FlowGqlResponse>(ATTRIBUTION_FLOW_QUERY, { ...baseVars, groupBy: `utmMedium` }),
      gqlQuery<FlowGqlResponse>(ATTRIBUTION_FLOW_QUERY, { ...baseVars, groupBy: `utmCampaign` }),
      gqlQuery<FlowGqlResponse>(ATTRIBUTION_FLOW_QUERY, { ...baseVars, groupBy: `channel` }),
    ])
      .then(([sourceRes, mediumRes, campaignRes, channelRes]) => {
        // Discard stale responses — a newer selection has already started a fresh fetch
        if (seq !== fetchSeqRef.current) return

        const toRow = (r: MarketingMetricsRow): AttributionRowType => ({
          bucket: r.bucket,
          leads: r.leads,
          mqls: r.mqls,
          appointments: r.appointments,
          patients: r.patients,
          production: r.production,
          scheduledProduction: r.scheduledProduction,
          googleSpend: r.googleSpend,
          facebookSpend: r.facebookSpend,
          totalSpend: (r.googleSpend ?? 0) + (r.facebookSpend ?? 0),
          ROI: r.roi,
          budget: r.budget,
        })
        // A bucket's channel is a property of the bucket, so the filtered rows take it from the unfiltered ones.
        const withChannelFrom = (initial: AttributionRowType[] | undefined) => {
          const channelByBucket = new Map((initial ?? []).map((r) => [r.bucket, r.channel]))
          return (row: AttributionRowType): AttributionRowType => ({ ...row, channel: channelByBucket.get(row.bucket) })
        }
        const action: FlowAction = {
          type: `SET_FILTERED_DATA`,
          payload: {
            sourceRows: (sourceRes.data?.marketingMetrics.hits ?? [])
              .map(toRow)
              .map(withChannelFrom(initialSourceRows)),
            mediumRows: (mediumRes.data?.marketingMetrics.hits ?? [])
              .map(toRow)
              .map(withChannelFrom(initialMediumRows)),
            campaignRows: (campaignRes.data?.marketingMetrics.hits ?? [])
              .map(toRow)
              .map(withChannelFrom(initialCampaignRows)),
            channelRows: (channelRes.data?.marketingMetrics.hits ?? []).map(toRow),
            summary: sourceRes.data?.marketingMetrics.summary,
          },
        }
        dispatch(action)
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setFetchLoading(false)
      })
  }, [
    state.selectedSource,
    state.selectedMedium,
    state.selectedCampaign,
    state.status,
    state.isLoading,
    fromDate,
    toDate,
    locationIds,
    integrationStatus,
  ])

  const handleSelect = useCallback((columnType: ColumnType, bucket: string | null) => {
    if (columnType === `sources`) {
      if (bucket === null) {
        dispatch({ type: `DESELECT_SOURCE` })
      } else {
        dispatch({ type: `SELECT_SOURCE`, payload: bucket })
      }
    } else if (columnType === `mediums`) {
      if (bucket === null) {
        dispatch({ type: `DESELECT_MEDIUM` })
      } else {
        dispatch({ type: `SELECT_MEDIUM`, payload: bucket })
      }
    } else if (columnType === `campaigns`) {
      if (bucket === null) {
        dispatch({ type: `DESELECT_CAMPAIGN` })
      } else {
        dispatch({ type: `SELECT_CAMPAIGN`, payload: bucket })
      }
    }
  }, [])

  const clearFilters = useCallback(() => {
    dispatch({ type: `CLEAR_FILTERS` })
  }, [])

  const sourceRows = selectSourceRows(state, initialSourceRows)
  const mediumRows = selectMediumRows(state, initialMediumRows)
  const campaignRows = selectCampaignRows(state, initialCampaignRows)
  const stats = selectStats(state, initialSummary, initialChannelRows)
  const hasFilters = selectHasFilters(state)
  const isSourcesFiltered = selectIsFiltered(state, `sources`)
  const isMediumsFiltered = selectIsFiltered(state, `mediums`)
  const isCampaignsFiltered = selectIsFiltered(state, `campaigns`)

  const isLoading = state.isLoading || fetchLoading

  return {
    sourceRows,
    mediumRows,
    campaignRows,
    selectedSource: state.selectedSource,
    selectedMedium: state.selectedMedium,
    selectedCampaign: state.selectedCampaign,
    isSourcesLoading: isLoading,
    isMediumsLoading: isLoading,
    isCampaignsLoading: isLoading,
    isSourcesFiltered,
    isMediumsFiltered,
    isCampaignsFiltered,
    stats,
    hasFilters,
    handleSelect,
    clearFilters,
  }
}

// ============================================
// COMPONENT
// ============================================

export type ColumnProps = {
  title: string
  columnType: ColumnType
  rows: AttributionRowType[] | undefined
  selectedValue: string | null
  onSelect: (columnType: ColumnType, bucket: string | null) => void
  isLoading?: boolean
  isFiltered?: boolean
}

function channelPipeStyle(channel: string | undefined): CSSProperties | undefined {
  const hue = channel ? (CHANNEL_HUES[channel] ?? SMC_CHANNEL_GROUP_HUES[channel]) : undefined
  if (!hue) return undefined
  return { backgroundColor: chartHueStyles[hue].fillHover, borderColor: chartHueStyles[hue].stroke }
}

export function Column({ title, columnType, rows, selectedValue, onSelect, isLoading, isFiltered }: ColumnProps) {
  const displayRows = useMemo(() => {
    if (!rows) return []
    // A bucket with no leads in range leads nowhere, and dropping it also hides the
    // empty case variants the API returns as separate buckets. The data table keeps its zeros.
    const sorted = rows.filter((row) => row.leads > 0).sort((a, b) => b.leads - a.leads)
    if (selectedValue) {
      return sorted.filter((row) => row.bucket === selectedValue)
    }
    return sorted
  }, [rows, selectedValue])

  const handleClick = (bucket: string) => {
    if (selectedValue === bucket) {
      onSelect(columnType, null)
    } else {
      onSelect(columnType, bucket)
    }
  }

  return (
    <div className="relative flex max-h-[391px] min-w-[300px] flex-1 flex-col rounded-lg border border-default bg-surface p-3 md:h-[391px]">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-surface/70">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-strong border-t-blue-500" />
        </div>
      )}

      <div className="flex h-12 shrink-0 items-center px-3">
        <span className="flex-1 font-inter text-body-md font-semibold">{title}</span>
        <span className="w-16 text-center text-sm font-medium text-minimal">Leads</span>
        <span className="w-8"></span>
        <span className="w-16 text-center text-sm font-medium text-minimal">Patients</span>
      </div>

      <ScrollableList fill>
        {displayRows.map((row) => {
          const isSelected = selectedValue === row.bucket
          const isHighlighted = !isSelected && isFiltered
          const state = isSelected ? `selected` : isHighlighted ? `highlighted` : `default`

          return (
            <div
              key={row.bucket}
              data-state={state}
              onClick={() => handleClick(row.bucket)}
              className="group flex h-[59px] cursor-pointer items-center rounded-md border px-3 transition-all
                data-[state=selected]:border-l-[3px] data-[state=default]:border-default data-[state=highlighted]:border-[#2563EB1Ff] data-[state=selected]:border-success-strong
                data-[state=default]:bg-surface data-[state=highlighted]:bg-blue-600/[0.06] data-[state=selected]:bg-emerald-50
                data-[state=default]:hover:border-strong data-[state=default]:hover:bg-surface-subtle data-[state=highlighted]:hover:bg-blue-600/10 data-[state=selected]:hover:bg-emerald-100"
            >
              <span
                className={classNames(`mr-2.5 h-6 w-1 shrink-0 rounded-full`, row.channel && `border`)}
                style={channelPipeStyle(row.channel)}
              />
              <span
                className="line-clamp-2 flex-1 break-words pr-2 text-sm font-semibold capitalize leading-tight
                text-default group-data-[state=highlighted]:text-default group-data-[state=selected]:text-success-strong"
              >
                {row.bucket || `(none)`}
              </span>
              <span
                className="w-16 text-center text-sm font-medium
                text-default group-data-[state=highlighted]:text-info-strong group-data-[state=selected]:text-success-strong"
              >
                {row.leads.toLocaleString()}
              </span>
              <span
                className="w-8 text-center text-xs
                text-minimal group-data-[state=highlighted]:text-info-strong group-data-[state=selected]:text-success-strong"
              >
                →
              </span>
              <span
                className="w-16 text-center text-sm font-medium
                text-success-default group-data-[state=highlighted]:text-info-strong group-data-[state=selected]:text-success-strong"
              >
                {row.patients.toLocaleString()}
              </span>
            </div>
          )
        })}
        {displayRows.length === 0 && <div className="py-8 text-center text-sm text-minimal">No data</div>}
      </ScrollableList>
    </div>
  )
}

function buildStatsCards(
  stats: {
    leads: number
    appointments: number
    patients: number
    production: number
  },
  handlers: {
    onClickLeads: () => void
    onClickAppointments: () => void
    onClickPatients: () => void
    onClickNetProduction: () => void
  },
): StatsCardType[] {
  return [
    { label: `Leads`, value: stats.leads.toLocaleString(), onOpenClick: handlers.onClickLeads },
    { label: `Appointments`, value: stats.appointments.toLocaleString(), onOpenClick: handlers.onClickAppointments },
    { label: `Patients`, value: stats.patients.toLocaleString(), onOpenClick: handlers.onClickPatients },
    { label: `Net Production`, value: formatCurrency(stats.production), onOpenClick: handlers.onClickNetProduction },
  ]
}

type AttributionFlowProps = UseAttributionFlowDataProps

export default function AttributionFlow(props: AttributionFlowProps) {
  const [, setSearchParams] = useSearchParams()

  const {
    sourceRows,
    mediumRows,
    campaignRows,
    selectedSource,
    selectedMedium,
    selectedCampaign,
    isSourcesLoading,
    isMediumsLoading,
    isCampaignsLoading,
    isSourcesFiltered,
    isMediumsFiltered,
    isCampaignsFiltered,
    stats,
    handleSelect,
  } = useAttributionFlowData(props)

  const closeDetails = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete(`details`)
      return prev
    })
  }, [setSearchParams])

  const openContactsModal = useCallback(
    (title: string) => {
      setSearchParams((prev) => {
        prev.set(`details`, title)
        return prev
      })
      showModal(
        <MarketingContactTable
          title={title}
          filterContactsBy={title}
          fromDate={props.fromDate}
          toDate={props.toDate}
          locationIds={props.locationIds}
          integrationStatus={props.integrationStatus}
          utmSource={selectedSource ? `utmSource.keyword:"${selectedSource}"` : undefined}
          utmMedium={selectedMedium ? `utmMedium.keyword:"${selectedMedium}"` : undefined}
          utmCampaign={selectedCampaign ? `utmCampaign.keyword:"${selectedCampaign}"` : undefined}
          onClose={closeDetails}
          enableDeepLinking={false}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: closeDetails,
          useRadix: true,
        },
      )
    },
    [
      props.fromDate,
      props.toDate,
      props.locationIds,
      props.integrationStatus,
      selectedSource,
      selectedMedium,
      selectedCampaign,
      closeDetails,
      setSearchParams,
    ],
  )

  const openRevenueModal = useCallback(() => {
    setSearchParams((prev) => {
      prev.set(`details`, `Net Production`)
      return prev
    })
    const handleClose = () => {
      closeModal()
      closeDetails()
    }
    showModal(
      <RevenueDetails
        filterTransactionsBy="all"
        fromDate={props.fromDate}
        toDate={props.toDate}
        locationIds={props.locationIds}
        title="Net Production"
        onClose={handleClose}
      />,
      {
        hideTitle: true,
        removePadding: true,
        isFullScreen: true,
        onClose: handleClose,
        useRadix: true,
      },
    )
  }, [props.fromDate, props.toDate, props.locationIds, closeDetails, setSearchParams])

  return (
    <div className="flex w-full flex-col">
      <SectionHeader
        title="Attribution Flow"
        description="Click any item to filter and explore the full attribution path."
      />

      <div className="mx-auto w-full max-w-[1680px]">
        <StatsCards
          stats={buildStatsCards(stats, {
            onClickLeads: () => openContactsModal(`Leads`),
            onClickAppointments: () => openContactsModal(`Appointments`),
            onClickPatients: () => openContactsModal(`Patients`),
            onClickNetProduction: () => openRevenueModal(),
          })}
        />
      </div>

      <div className="mx-auto flex w-full max-w-[1680px] flex-wrap gap-4">
        <Column
          title="Sources"
          columnType="sources"
          rows={sourceRows}
          selectedValue={selectedSource}
          onSelect={handleSelect}
          isLoading={isSourcesLoading}
          isFiltered={isSourcesFiltered && !selectedSource}
        />
        <Column
          title="Mediums"
          columnType="mediums"
          rows={mediumRows}
          selectedValue={selectedMedium}
          onSelect={handleSelect}
          isLoading={isMediumsLoading}
          isFiltered={isMediumsFiltered && !selectedMedium}
        />
        <Column
          title="Campaigns"
          columnType="campaigns"
          rows={campaignRows}
          selectedValue={selectedCampaign}
          onSelect={handleSelect}
          isLoading={isCampaignsLoading}
          isFiltered={isCampaignsFiltered && !selectedCampaign}
        />
      </div>
    </div>
  )
}
